/**
 * Bruit de gradient 2D (type Perlin amélioré) et dérivés (fbm, ridged).
 *
 * Uniquement des opérations arithmétiques de base (+, -, *, floor) : le
 * résultat est identique au bit près entre Node et les navigateurs, ce qui
 * garantit que client et serveur voient exactement le même relief.
 */
import { Rng } from './rng';

export type Noise2D = (x: number, y: number) => number;

const GX = [1, -1, 1, -1, 1, -1, 0, 0];
const GY = [1, 1, -1, -1, 0, 0, 1, -1];

function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

export function createNoise2D(seed: number): Noise2D {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  const rng = new Rng(seed);
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    const tmp = p[i] as number;
    p[i] = p[j] as number;
    p[j] = tmp;
  }
  const perm = new Uint8Array(512);
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255] as number;

  return (x: number, y: number): number => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const xf = x - xi;
    const yf = y - yi;
    const X = xi & 255;
    const Y = yi & 255;
    const a = perm[X] as number;
    const b = perm[X + 1] as number;
    const g00 = (perm[a + Y] as number) & 7;
    const g01 = (perm[a + Y + 1] as number) & 7;
    const g10 = (perm[b + Y] as number) & 7;
    const g11 = (perm[b + Y + 1] as number) & 7;
    const n00 = (GX[g00] as number) * xf + (GY[g00] as number) * yf;
    const n10 = (GX[g10] as number) * (xf - 1) + (GY[g10] as number) * yf;
    const n01 = (GX[g01] as number) * xf + (GY[g01] as number) * (yf - 1);
    const n11 = (GX[g11] as number) * (xf - 1) + (GY[g11] as number) * (yf - 1);
    const u = fade(xf);
    const v = fade(yf);
    const nx0 = n00 + u * (n10 - n00);
    const nx1 = n01 + u * (n11 - n01);
    return nx0 + v * (nx1 - nx0);
  };
}

/** Somme fractale (fbm), sortie approximativement dans [-1, 1]. */
export function fbm(noise: Noise2D, x: number, y: number, octaves: number, lacunarity = 2, gain = 0.5): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let fx = x;
  let fy = y;
  for (let o = 0; o < octaves; o++) {
    sum += amp * noise(fx, fy);
    norm += amp;
    amp *= gain;
    fx = fx * lacunarity + 17.13;
    fy = fy * lacunarity - 9.71;
  }
  return sum / norm;
}

/** Bruit « crêtes » : 1 au sommet des arêtes, sortie dans [0, 1]. */
export function ridged(noise: Noise2D, x: number, y: number, octaves: number): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let fx = x;
  let fy = y;
  let weight = 1;
  for (let o = 0; o < octaves; o++) {
    let n = 1 - Math.abs(noise(fx, fy));
    n *= n;
    n *= weight;
    weight = Math.min(1, Math.max(0, n * 2));
    sum += n * amp;
    norm += amp;
    amp *= 0.5;
    fx = fx * 2.03 + 3.1;
    fy = fy * 2.03 - 7.7;
  }
  return sum / norm;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Onde périodique lisse (période 1, sortie [-1, 1]) sans fonction transcendante. */
export function swave(x: number): number {
  const t = x - Math.floor(x);
  const tri = t < 0.5 ? t * 2 : 2 - t * 2;
  const s = tri * tri * (3 - 2 * tri);
  return s * 2 - 1;
}
